import { useIntl } from 'umi';
import * as React from 'react';

export const INTL_ENTITY = {
  APPOINTMENT: 'appointment',
  CONTACT: 'contact',
  TASK: 'task',
  VIEW: 'view',
  AGENT: 'agent',
  OPTION: 'option',
  EXTRA_FIELD: 'extra_field',
  ROLE: 'role',
  USER: 'user',
  LEAD: 'lead',
  COMMENT: 'comment',
  LEAD_STATE: 'lead_state',
  ACTIVITY: 'activity',
  FILE: 'file',
  LEAD_SOURCE: 'lead_source',
  WEBHOOK: 'webhook',
  TAG: 'tag',
  TAG_GROUP: 'tag_group',
  IMAGE: 'image',
  EMAIL_AUTOMATION: 'email_automation',
  TASK_AUTOMATION: 'task_automation',
  CONSEQUENCE: 'consequence',
  RETIRE_TRIGGER: 'retire_trigger',
  ACTION_BUTTON: 'action_button',
  SERVICE_TYPE: 'service_type',
  BRANCH: 'branch',
  SERVICE: 'service',
  BLOCK_PERIOD: 'block_period',
  ADDITIONAL_OPEN_HOURS: 'additional_open_hours',
  WORKING_HOURS: 'working_hours',
  PRODUCT_GROUP: 'product_group',
  SUPPORT_TICKET: 'support_ticket',
  BOOKING_PAGE: 'booking_page',
  API_KEY: 'api_key',
  OFFERING: 'offering',
  EMAIL_TEMPLATE: 'email_template',
  MAILER: 'mailer',
  MARKETING_AUTOMATION: 'marketing_automation',
  PARTNER_TRANSLATION: 'partner_translation',
  CALENDAR_EVENT: 'calendar_event',
  CUSTOMER: 'customer',
  KITCHEN_PLANNING: 'kitchen_planning',
  MAPPING: 'mapping',
  GOAL: 'goal',
  INTEGRATION: 'integration',
  SKILL: 'skill',
} as const;

export const INTL_ATTRIBUTE = {
  CREATED_AT: 'created_at',
  UPDATED_AT: 'updated_at',
} as const;


export const INTL_ACTION = {
  CREATE: 'create',
  CREATE_FAILED: 'create_failed',
  CREATE_SUCCESS: 'create_success',
  EDIT: 'edit',
  EDIT_FAILED: 'edit_failed',
  EDIT_SUCCESS: 'edit_success',
  ADD: 'add',
  NEW: 'new',
  ADD_SUCCESS: 'add_success',
  ADD_FAILED: 'add_failed',
  REMOVE: 'remove',
  REMOVE_CONFIRMATION: 'remove_confirmation',
  REMOVE_FAILED: 'remove_failed',
  REMOVE_SUCCESS: 'remove_success',
  LIST: 'list',
  COUNT: 'count',
  GO_TO: 'go_to',
  SAVE: 'save',
  SELECT: 'select',
  SELECT_PLURAL: 'select_plural',
  VIEW: 'view',
  VIEW_PLURAL: 'view_plural',
  DETAILS: 'details',
  EMPTY: 'empty',
  NO: 'no',
  SEARCH: 'search',
  ALL: 'all',
} as const;

export function getIntlTranslation(intl, {
  id,
  defaultMessage,
  className,
  values = {},
  entity,
  attribute,
  action,
  ...props
}: {
  id?: string,
  defaultMessage?: string,
  className?: string,
  values?: Record<string, any>,
  entity?: typeof INTL_ENTITY[keyof typeof INTL_ENTITY],
  attribute?: string,
  action?: typeof INTL_ACTION[keyof typeof INTL_ACTION],
  [key: string]: any,
}) {
  if (entity && action) {
    return intl.formatMessage(
      { id: `common.entity.${action}`, defaultMessage },
      {
        entity: intl.formatMessage({ id: `entity.${entity}.singular`, defaultMessage: entity }),
        entity_plural: intl.formatMessage({ id: `entity.${entity}.plural`, defaultMessage: entity }),
        ...values,
      });
  } else if (entity && attribute) {
    return intl.formatMessage(
      { id: 'common.entity.attribute', defaultMessage: '{entity} {attribute}' },
      {
        entity: intl.formatMessage({ id: `entity.${entity}.singular`, defaultMessage: entity }),
        attribute: intl.formatMessage({ id: `common.attribute.${attribute}`, defaultMessage: attribute }),
        ...values,
      });
  } else {
    if (!id) return " - "
    return intl.formatMessage({
      id,
      defaultMessage: defaultMessage || id,
    }, values);
  }
}

export function F({ id, defaultMessage, className, values = {}, entity, attribute, action, ...props }: {
  id?: string,
  defaultMessage?: string | React.ReactNode,
  values?: any,
  entity?: typeof INTL_ENTITY[keyof typeof INTL_ENTITY],
  attribute?: typeof INTL_ATTRIBUTE[keyof typeof INTL_ATTRIBUTE],
  action?: typeof INTL_ACTION[keyof typeof INTL_ACTION],
  className?: string
}) {
  const intl = useIntl();
  let content = getIntlTranslation(intl, {
    id,
    defaultMessage,
    className,
    values,
    entity,
    attribute,
    action, ...props,
  });

  return <span className={className} {...props}>
    {content}
  </span>;
}

function recursiveTransform(path: string, value, result: Object) {
  if (typeof value === 'object' && !Array.isArray(value)) {
    for (let key in value) {
      recursiveTransform(`${path}.${key}`, value[key], result);
    }
  } else if (Array.isArray(value)) {
    value.forEach((item, index) => {
      recursiveTransform(`${path}[${index}]`, item, result);
    });
  } else {
    result[path] = String(value);
  }
}

export function transformObjectToLocaleMap(prefix: string, obj) {
  let result = {};

  function recursiveTransform(path, value) {
    if (typeof value === 'object' && !Array.isArray(value)) {
      for (let key in value) {
        recursiveTransform(`${path}.${key}`, value[key]);
      }
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        recursiveTransform(`${path}[${index}]`, item);
      });
    } else {
      result[path] = String(value);
    }
  }

  recursiveTransform(prefix, obj);
  return result;
}


export function fixLocalesData(localesData) {
  return localesData.map(localeData => {
    if (localeData.lang === "sl-SI")
      localeData.icon = '🇸🇮'
    if (localeData.lang === "sr-RS")
      localeData.icon = '🇷🇸';
    return localeData;
  })
}

export function filterHelpCenterLocales(localesData) {
  const allowedLocales = [
    'en-US', 'de-DE', 'cs-CZ', 'sk-SK', 'sl-SI', 'hr-HR', 'ro-RO',
    'nl-NL', 'es-ES', 'fr-FR', 'hu-HU', 'sr-RS', 'sv-SE', 'it-IT',
  ];
  return fixLocalesData(localesData).filter(locale => allowedLocales.includes(locale.lang));
}

export function extractLanguageCode(locale: string): string {
  if (!locale) return locale;
  return locale.split('-')[0];
}
